import TemplateClassificationsAPI from '../../api/templateClassifications';
import TemplateSectionsAPI from '../../api/templateSections';

// Estado POR BANDEJA: cada bandeja tiene sus secciones personalizadas (nombre
// + orden) y sus plantillas hidratadas (section_id, position, display_name).
const state = {
  byInbox: {}, // { [inboxId]: { sections: [], templates: [] } }
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isRefreshing: false,
  },
};

const emptyInboxData = () => ({ sections: [], templates: [] });

export const getters = {
  getSections: $state => inboxId =>
    ($state.byInbox[inboxId] || emptyInboxData()).sections,
  getTemplates: $state => inboxId =>
    ($state.byInbox[inboxId] || emptyInboxData()).templates,
  isInboxLoaded: $state => inboxId => Boolean($state.byInbox[inboxId]),
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  get: async ({ commit }, { inboxId }) => {
    if (!inboxId) return;
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await TemplateClassificationsAPI.get(inboxId);
      commit('SET_INBOX_DATA', {
        inboxId,
        sections: data.sections || [],
        templates: data.templates || [],
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[templateClassifications] fetch error:', error);
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },
  // Refresca todas las bandejas ya cargadas (usado por el WS cuando el evento
  // no trae inbox_id) o solo la afectada.
  refreshLoaded: async ({ state: $state, dispatch }, { inboxId } = {}) => {
    const loaded = Object.keys($state.byInbox).map(Number);
    const targets = inboxId ? loaded.filter(id => id === inboxId) : loaded;
    await Promise.all(targets.map(id => dispatch('get', { inboxId: id })));
  },
  refreshAll: async ({ commit, dispatch, state: $state }) => {
    commit('SET_UI_FLAG', { isRefreshing: true });
    try {
      const { data } = await TemplateClassificationsAPI.refreshAll();
      // eslint-disable-next-line no-console
      console.log('[templateClassifications] refresh triggered:', data);
      // Templates land asynchronously in DB after Twilio responds — re-fetch
      // a few seconds later to surface any newly created template names.
      setTimeout(() => {
        Object.keys($state.byInbox).forEach(id =>
          dispatch('get', { inboxId: Number(id) })
        );
      }, 4000);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[templateClassifications] refresh error:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isRefreshing: false });
    }
  },
  rename: async (
    { commit, state: $state },
    { inboxId, templateName, displayName }
  ) => {
    commit('SET_UI_FLAG', { isUpdating: true });
    // Actualización optimista del alias local.
    const data = $state.byInbox[inboxId];
    if (data) {
      commit('SET_INBOX_DATA', {
        inboxId,
        sections: data.sections,
        templates: data.templates.map(t =>
          t.name === templateName
            ? { ...t, display_name: displayName || null }
            : t
        ),
      });
    }
    try {
      await TemplateClassificationsAPI.rename(
        inboxId,
        templateName,
        displayName
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[templateClassifications] rename error:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },
  reorder: async ({ commit, state: $state }, { inboxId, items }) => {
    commit('SET_UI_FLAG', { isUpdating: true });
    // Optimistic local update
    const data = $state.byInbox[inboxId];
    if (data) {
      commit('SET_INBOX_DATA', {
        inboxId,
        sections: data.sections,
        templates: data.templates.map(t => {
          const updated = items.find(i => i.template_name === t.name);
          if (!updated) return t;
          return {
            ...t,
            section_id: updated.section_id,
            position: updated.position,
          };
        }),
      });
    }
    try {
      await TemplateClassificationsAPI.reorder(inboxId, items);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[templateClassifications] reorder error:', error);
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },
  createSection: async ({ dispatch }, { inboxId, name }) => {
    const { data } = await TemplateSectionsAPI.create(inboxId, name);
    // eslint-disable-next-line no-console
    console.log('[templateClassifications] section created:', data);
    await dispatch('get', { inboxId });
    return data;
  },
  renameSection: async ({ dispatch }, { inboxId, id, name }) => {
    await TemplateSectionsAPI.rename(inboxId, id, name);
    await dispatch('get', { inboxId });
  },
  reorderSections: async (
    { commit, state: $state },
    { inboxId, orderedIds }
  ) => {
    // Optimista: reordenamos la lista local de secciones.
    const data = $state.byInbox[inboxId];
    if (data) {
      const byId = new Map(data.sections.map(s => [s.id, s]));
      commit('SET_INBOX_DATA', {
        inboxId,
        sections: orderedIds
          .map((id, idx) => ({ ...byId.get(id), position: idx }))
          .filter(s => s.id),
        templates: data.templates,
      });
    }
    await TemplateSectionsAPI.reorder(inboxId, orderedIds);
  },
  destroySection: async ({ dispatch }, { inboxId, id }) => {
    await TemplateSectionsAPI.destroy(inboxId, id);
    await dispatch('get', { inboxId });
  },
};

export const mutations = {
  SET_INBOX_DATA($state, { inboxId, sections, templates }) {
    $state.byInbox = {
      ...$state.byInbox,
      [inboxId]: { sections, templates },
    };
  },
  SET_UI_FLAG($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
